<template>
    <!-- Waitlist Modal -->
    <BaseModal
        :model-value="modelValue"
        :icon="mdiPlaylistPlus"
        color="blue-lighten-5"
        title="Join the Waitlist"
        @update:model-value="emit('update:modelValue', $event)"
    >
        <template #body>
            <v-card-text>
                <v-form
                    ref="form"
                    v-model="formValid"
                    @submit.prevent="submitWaitlist"
                >
                    <v-text-field
                        v-model="waitlistForm.name"
                        label="Name"
                        aria-label="Name"
                        :rules="[rules.required]"
                        required
                        class="mb-4"
                    />

                    <v-text-field
                        v-model="waitlistForm.email"
                        label="Email Address"
                        aria-label="Email Address"
                        :rules="[rules.required, rules.email]"
                        required
                        type="email"
                        class="mb-4"
                    />

                    <v-select
                        v-model="waitlistForm.studentCount"
                        label="Size of Studio"
                        aria-label="Size of Studio"
                        :items="studentCountOptions"
                        :rules="[rules.required]"
                        required
                        class="mb-4"
                    />

                    <v-select
                        v-model="waitlistForm.studioType"
                        label="Type of Studio"
                        aria-label="Type of Studio"
                        :items="studioTypeOptions"
                        :rules="[rules.required]"
                        required
                        class="mb-4"
                    />

                    <v-text-field
                        v-if="waitlistForm.studioType === 'Other'"
                        v-model="waitlistForm.customStudioType"
                        label="Please specify your studio type"
                        aria-label="Please specify your studio type"
                        :rules="
                            waitlistForm.studioType === 'Other'
                                ? [rules.required]
                                : []
                        "
                        class="mb-4"
                    />

                    <v-select
                        v-model="waitlistForm.betaInvolvementLevels"
                        label="Beta involvement (optional)"
                        aria-label="Beta involvement (optional)"
                        :items="betaInvolvementOptions"
                        multiple
                        chips
                        class="mb-4"
                    />
                </v-form>
            </v-card-text>
        </template>

        <template #actions>
            <v-spacer />
            <v-btn
                variant="text"
                :disabled="submitting"
                @click="closeDialog"
            >
                Cancel
            </v-btn>
            <v-btn
                color="blue"
                variant="elevated"
                :loading="submitting"
                :disabled="!formValid"
                :prepend-icon="mdiPlaylistPlus"
                @click="submitWaitlist"
            >
                Join Waitlist
                <template #loader>
                    <NamedLoader
                        label="Joining waitlist"
                        width="2"
                    />
                </template>
            </v-btn>
        </template>
    </BaseModal>

    <!-- Success Dialog -->
    <SuccessDialog
        v-model="successDialog"
        title="Thank You!"
        message="You've been added to our waitlist. We'll be in touch soon!"
    />
</template>

<script lang="ts" setup>
import { mdiPlaylistPlus } from '@mdi/js';
import { WaitlistEntryId } from '@studoku/shared';
import { ulid } from 'ulid';
import * as v from 'valibot';
import { ref } from 'vue';
import { VForm } from 'vuetify/components/VForm';

import SuccessDialog from '@/shared/components/dialogs/SuccessDialog.vue';
import BaseModal from '@/shared/components/modals/_BaseModal.vue';
import NamedLoader from '@/shared/components/NamedLoader.vue';
import { http } from '@/shared/http';

interface Props {
    modelValue: boolean;
}

interface Emits {
    (e: 'update:modelValue', value: boolean): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const form = ref<VForm>();
const formValid = ref(false);
const submitting = ref(false);
const successDialog = ref(false);

const STUDENT_COUNT_OPTIONS = [
    '0-5 students',
    '5-15 students',
    '15-30 students',
    '30-60 students',
    '60+ students',
] as const;

const STUDIO_TYPE_OPTIONS = [
    'Music',
    'Academic Tutoring',
    'Art',
    'Dance',
    'Drama/Theater',
    'Language',
    'Martial Arts',
    'Other',
] as const;

const BETA_INVOLVEMENT_OPTIONS = [
    'Phone/video calls and feedback sessions',
    'Written surveys and email feedback',
    'Product updates and notifications',
] as const;

const waitlistForm = ref({
    name: '',
    email: '',
    studentCount: null,
    studioType: null,
    customStudioType: '',
    betaInvolvementLevels: [],
});

const studentCountOptions = [...STUDENT_COUNT_OPTIONS];
const studioTypeOptions = [...STUDIO_TYPE_OPTIONS];
const betaInvolvementOptions = [...BETA_INVOLVEMENT_OPTIONS];

const rules = {
    required: (value: string | null) => !!value || 'Required.',
    requiredArray: (value: unknown[]) =>
        (value && value.length > 0) || 'Please select at least one option.',
    email: (value: string) => {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return pattern.test(value) || 'Invalid email address.';
    },
};

async function submitWaitlist() {
    if (!formValid.value) return;

    submitting.value = true;

    try {
        await http('POST /api/waitlist', {
            ...waitlistForm.value,
            id: v.parse(WaitlistEntryId, ulid()),
            submittedAt: new Date(),
            studentCount: waitlistForm.value.studentCount || '',
            studioType: waitlistForm.value.studioType || '',
        });

        emit('update:modelValue', false);
        successDialog.value = true;
        resetForm();
    } catch (error) {
        console.error('Error joining waitlist:', error);
    } finally {
        submitting.value = false;
    }
}

function closeDialog() {
    emit('update:modelValue', false);
    resetForm();
}

function resetForm() {
    waitlistForm.value = {
        name: '',
        email: '',
        studentCount: null,
        studioType: null,
        customStudioType: '',
        betaInvolvementLevels: [],
    };
    if (form.value) {
        form.value.resetValidation();
    }
}
</script>
